import React, { useEffect, useState, useMemo, useRef } from 'react';

import { ComboBox, SelectableOptionMenuItemType } from '@fluentui/react';

import { mergeLists, MergeListsAction } from 'shared/utils/utils';

import { useConversion, useSubstitutionPicker } from 'src/components/base/fields/utils';
import { SubstitutionPicker as SubstitutionPicker, classNames } from 'src/components/base/fields/SubstitutionPicker';


const Data = {
  separator: ',',
  selectAllKey: 'my-select-all-key',
};


export const TextConverters = {
  parse: (text, isMultiselect, separator) => ({ 
    value: (isMultiselect ? text.split(separator) : [text]).map(x => x.trim()).filter(Boolean) 
  }),
  format: (selectedItems, isMultiselect, separator) => ({ 
    value: selectedItems.map(x => x.value).join(separator) 
  }),
  getDefault: (isMultiselect, options) => isMultiselect
    ? []    
    : options.length ? [options[0].key] : [],
};


export function MultiselectField({ converters, ...props }) {
  const { isMultiselect, options } = props;
  const cvt = mergeConverters(converters, { options, isMultiselect });

  const { value, substitution, onChange } = useConversion(props, cvt);
  const sProps = useSubstitutionPicker(props);

  return (
    <div className={classNames.withTargetPicker}>
      <MultiselectFieldBase 
        {...props} 
        substitution={substitution}
        selectedKeys={value}
        onChange={onChange} 
      /> 
      <SubstitutionPicker {...sProps} />

    </div>
  );
}


export function MultiselectFieldBase({ 
  text, substitution, options, selectedKeys = [], isMultiselect, onChange, disabled 
}) {
  const comboboxOptions = useMemo(() => {
    const selectAll = isMultiselect
      ? [{ key: Data.selectAllKey, text: 'Select All', itemType: SelectableOptionMenuItemType.SelectAll },
         { key: '-dvd-', text: '-', itemType: SelectableOptionMenuItemType.Divider }]
      : [];

    return options.reduce((out, data) => {
      const { id, key, text } = data;
      out.push(id
        ? { id, key, text, data, selected: !!selectedKeys.find(y => y === key) }
        : data);
      return out;
    }, selectAll);
  }, [options, selectedKeys, isMultiselect]);

  const allSelected = isMultiselect && selectedKeys.length === options.length;
  const selectedKey = selectedKeys.length ? selectedKeys[0] : '';

  function onOptionsSelected(values) {
    onChange(values);
  }

  function handleItemsSelected(event, item) {
    const { data, itemType, selected } = item;

    if (itemType === SelectableOptionMenuItemType.SelectAll) {
      const values = !selected ? [] : options;
      onOptionsSelected(values);
    }
    else if (!itemType) {
      const selectedData = comboboxOptions.reduce((out, x) => {
        x.selected && out.push(x.data);
        return out;
      }, []);

      const action = selected ? MergeListsAction.Add : MergeListsAction.Remove;
      const values = isMultiselect
        ? mergeLists(selectedData, [data], action, (a, b) => a.id === b.id)
        : data ? [data] : [];
      onOptionsSelected(values);
    }
  }
  
  return (
    <ComboBox
      placeholder="Select Value"
      useComboBoxAsMenuWidth={true}
      multiSelect={isMultiselect}
      selectedKey={allSelected ? [...selectedKeys, Data.selectAllKey] : selectedKey}
      onChange={handleItemsSelected}
      options={comboboxOptions}
      text={substitution ?? text}
      disabled={disabled}
      styles={{ container: { width: '100%' } }}
    />
  );
}


export function mergeConverters(converters, { options = [], isMultiselect = false, separator = Data.separator }) {
  const {
    parse = (text, parseSubstitution) => TextConverters.parse(text, isMultiselect, separator),
    format = (selectedItems, parseSubstitution) => TextConverters.format(selectedItems, isMultiselect, separator),
    getDefault = () => TextConverters.getDefault(isMultiselect, options),
  } = converters ?? {};
  return { parse, format, getDefault };
}


export default MultiselectField;